import { useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { BadgeEuro, CalendarClock, Handshake, Users } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

import { FaqList, type FaqItem } from "@/components/site/FaqList";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { SiteLayout } from "@/components/site/SiteLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trackB2bSignupStart } from "@/lib/b2b-public.functions";
import { partnerSignup } from "@/lib/partner.functions";
import { PARTNER_TYPES } from "@/lib/partners-shared";

const BENEFITS = [
  {
    icon: BadgeEuro,
    title: "Commissie per rit",
    text: "U ontvangt een vaste commissie voor iedere betaalde rit die u boekt of die via uw partnercode wordt geboekt.",
  },
  {
    icon: Users,
    title: "Uw klant blijft aan u gekoppeld",
    text: "Boekt uw klant later opnieuw een rit, ook zonder uw code, dan blijft die rit aan uw bedrijf gekoppeld en ontvangt u opnieuw commissie.",
  },
  {
    icon: CalendarClock,
    title: "In één minuut geboekt",
    text: "Vul de route en de gegevens van uw passagier in, bekijk direct de vaste prijs en rond de betaling af of stuur uw klant een betaallink.",
  },
  {
    icon: Handshake,
    title: "Alles op één plek",
    text: "In uw eigen portaal ziet u al uw ritten, klanten en commissie. U downloadt facturen op bedrijfsnaam en commissie wordt maandelijks uitbetaald.",
  },
];

const FAQ: FaqItem[] = [
  {
    q: "Wat kost het om partner te worden?",
    a: "Deelname is gratis. U betaalt alleen de rit die u boekt, tegen dezelfde vaste prijs die iedere klant ziet, en u ontvangt commissie over betaalde ritten.",
  },
  {
    q: "Hoe werkt mijn partnercode?",
    a: "U krijgt na goedkeuring een eigen partnercode. Uw klant vult die code in bij het boeken op Taxiritje.nl. De klant wordt daarna permanent aan uw bedrijf gekoppeld, ook bij volgende ritten.",
  },
  {
    q: "Kan mijn klant zelf betalen?",
    a: "Ja. U kiest per rit of u zelf direct afrekent of dat wij uw klant een beveiligde betaallink mailen. De rit wordt bevestigd en ingepland zodra de betaling is afgerond.",
  },
  {
    q: "Wanneer ontvang ik mijn commissie?",
    a: "Commissie ontstaat zodra een rit is betaald, wordt beschikbaar zodra de rit is uitgevoerd en wordt los van de ritbetalingen aan u overgemaakt. Bij een geannuleerde rit vervalt de commissie.",
  },
  {
    q: "Kunnen ook hotels en andere bedrijven meedoen?",
    a: "Ja. Naast reisbureaus werken wij met hotels, bedrijven, maritieme bedrijven, ship repair bedrijven, crewing agencies, scheepsagenten, recruitmentbedrijven en evenementenorganisaties.",
  },
  {
    q: "Hoe snel is mijn aanvraag goedgekeurd?",
    a: "Wij controleren iedere aanmelding handmatig. Zodra uw account is goedgekeurd ontvangt u een e-mail met uw partnercode en kunt u direct inloggen en boeken.",
  },
];

export function PartnerLandingPage() {
  const signup = useServerFn(partnerSignup);
  const [done, setDone] = useState<string | null>(null);
  const [form, setForm] = useState({
    companyName: "",
    partnerType: "reisbureau",
    kvkNumber: "",
    vatNumber: "",
    address: "",
    postcode: "",
    city: "",
    country: "Nederland",
    contactFirstName: "",
    contactLastName: "",
    contactEmail: "",
    phone: "",
    loginEmail: "",
    password: "",
    iban: "",
    accountHolder: "",
  });
  const [accepted, setAccepted] = useState(false);
  const trackSignupStart = useServerFn(trackB2bSignupStart);
  const trackedRef = useRef(false);

  // Kwam deze bezoeker via een zakelijke campagnemail? Dan leggen wij eenmalig
  // vast dat de aanmelding is gestart, zodat wij het resultaat kunnen meten.
  const markSignupStarted = () => {
    if (trackedRef.current || typeof window === "undefined") return;
    const token = new URLSearchParams(window.location.search).get("b2b");
    if (!token || !/^[a-f0-9]{16,64}$/i.test(token)) return;
    trackedRef.current = true;
    void trackSignupStart({ data: { token } }).catch(() => undefined);
  };

  const mutation = useMutation({
    mutationFn: () => signup({ data: form }),
    onSuccess: (result) => {
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      setDone(result.referralCode);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const field = (
    key: keyof typeof form,
    label: string,
    opts?: { type?: string; optional?: boolean },
  ) => (
    <div>
      <Label htmlFor={`veld-${key}`} className="text-sm font-medium">
        {label}
        {opts?.optional ? " (optioneel)" : ""}
      </Label>
      <Input
        id={`veld-${key}`}
        className="mt-1"
        type={opts?.type ?? "text"}
        required={!opts?.optional}
        value={form[key]}
        onFocus={markSignupStarted}
        onChange={(e) => {
          markSignupStarted();
          setForm((f) => ({ ...f, [key]: e.target.value }));
        }}
      />
    </div>
  );

  return (
    <SiteLayout>
      <div className="mx-auto max-w-4xl px-4 py-10">
        <PageBreadcrumb current="Voor reisbureaus" />
        <h1 className="mt-3 font-display text-3xl font-bold sm:text-4xl">
          Taxivervoer voor zakelijke partners, met commissie per rit
        </h1>
        <p className="mt-3 text-base leading-relaxed text-foreground">
          Boekt u vervoer naar Schiphol, een station of een hotel voor uw reizigers? Met het
          zakelijke partnerprogramma van Taxiritje.nl boekt u ritten tegen een vaste prijs. U betaalt
          direct of stuurt uw klant een betaallink. Per betaalde rit ontvangt u commissie. Facturen
          downloadt u op bedrijfsnaam; commissie wordt maandelijks uitbetaald.
        </p>

        <section className="mt-8 grid gap-4 sm:grid-cols-2">
          {BENEFITS.map((b) => (
            <div key={b.title} className="card-elevated p-5">
              <b.icon className="size-5 text-primary" />
              <h2 className="mt-2 font-display text-base font-bold">{b.title}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{b.text}</p>
            </div>
          ))}
        </section>

        <section className="card-elevated mt-8 p-6">
          <h2 className="font-display text-xl font-bold">Hoe het werkt</h2>
          <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
            <li>U meldt uw bedrijf hieronder aan; wij controleren de aanvraag handmatig.</li>
            <li>Na goedkeuring ontvangt u uw eigen partnercode en toegang tot uw partnerportaal.</li>
            <li>
              U boekt ritten voor uw klanten, of uw klant vult uw partnercode in bij het boeken.
            </li>
            <li>
              Uw klant blijft daarna permanent aan uw bedrijf gekoppeld, ook bij volgende ritten.
            </li>
            <li>
              U volgt uw commissie in uw portaal en ontvangt uw uitbetaling los van de ritbetalingen.
            </li>
          </ol>
        </section>

        <section id="aanmelden" className="mt-10">
          <h2 className="font-display text-2xl font-bold">Aanmelden als zakelijke partner</h2>
          {done ? (
            <div className="card-elevated mt-4 p-6">
              <h3 className="font-display text-lg font-bold">Bedankt voor uw aanmelding</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Wij controleren uw aanvraag handmatig. Zodra uw account is goedgekeurd ontvangt u een
                e-mail en kunt u direct inloggen op uw partnerportaal. Uw partnercode wordt{" "}
                <strong className="text-foreground">{done}</strong>.
              </p>
              <Button className="mt-4" asChild>
                <Link to="/partner/login">Naar het partnerportaal</Link>
              </Button>
            </div>
          ) : (
            <form
              className="card-elevated mt-4 space-y-5 p-6"
              onSubmit={(e) => {
                e.preventDefault();
                mutation.mutate();
              }}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                {field("companyName", "Bedrijfsnaam")}
                <div>
                  <Label className="text-sm font-medium">Soort partner</Label>
                  <select
                    className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-base"
                    value={form.partnerType}
                    onChange={(e) => setForm((f) => ({ ...f, partnerType: e.target.value }))}
                  >
                    {PARTNER_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                {field("kvkNumber", "KVK-nummer")}
                {field("vatNumber", "Btw-nummer", { optional: true })}
                {field("address", "Straat en huisnummer")}
                {field("postcode", "Postcode")}
                {field("city", "Plaats")}
                {field("country", "Land")}
                {field("contactFirstName", "Voornaam contactpersoon")}
                {field("contactLastName", "Achternaam contactpersoon")}
                {field("contactEmail", "E-mailadres contactpersoon", { type: "email" })}
                {field("phone", "Telefoonnummer")}
                {field("loginEmail", "E-mailadres om in te loggen", { type: "email" })}
                {field("password", "Wachtwoord (minimaal 8 tekens)", { type: "password" })}
                {field("iban", "Rekeningnummer (IBAN) voor uw commissie")}
                {field("accountHolder", "Naam rekeninghouder")}
              </div>

              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                />
                <span>
                  Ik meld mijn bedrijf aan voor het zakelijke partnerprogramma van Taxiritje.nl en ga
                  ermee akkoord dat ritten altijd direct worden betaald en dat commissie pas ontstaat
                  bij een betaalde rit.
                </span>
              </label>

              <Button
                type="submit"
                size="lg"
                className="font-semibold"
                disabled={!accepted || mutation.isPending}
              >
                {mutation.isPending ? "Aanvraag versturen…" : "Aanmelding versturen"}
              </Button>
              <p className="text-xs text-muted-foreground">
                Heeft u al een partneraccount?{" "}
                <Link to="/partner/login" className="font-semibold underline">
                  Inloggen op uw partnerportaal
                </Link>
              </p>
            </form>
          )}
        </section>

        <section className="mt-10">
          <h2 className="font-display text-xl font-bold sm:text-2xl">Veelgestelde vragen</h2>
          <div className="card-elevated mt-4 p-4 sm:p-6">
            <FaqList items={FAQ} />
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}
